import {
  deepCopyDate,
  getFormattedDuration,
  getISODateString,
  getIstTimeFromTimestamp,
  getTimestampFromDatetime,
} from "@/methods/utils";
import { ServerClock, ServerClockStore } from "@/types/global";
import { differenceInSeconds } from "date-fns";
import { defineStore } from "pinia";
import { useApiServicesStore } from "@/stores/core/apiServicesStore";
import { v4 as uuidv4 } from "uuid";

// Module-scoped, never store state: a Promise must not be made reactive or persisted. Ties every
// concurrent caller to a single ping — performServerClockPing() opens with resetServerClock(),
// so two overlapping pings each zero the timestamp the other just published, and a waiter can be
// un-resolved by a request it never made.
let serverClockPingPromise: Promise<number> | null = null;

function getInitialServerClockStore(): ServerClockStore {
  const initialServerClockStore: ServerClockStore = {
    LastServerClock: null,
    ServerClockWorkerUUID: "",
    ServerClockWorker: null,
    _ServerTimestamp: 0,
  };
  return JSON.parse(JSON.stringify(initialServerClockStore));
}

export const useServerClockStore = defineStore("serverClockStore", {
  state: (): ServerClockStore => getInitialServerClockStore(),
  getters: {
    CurrentWorkerKey(): string {
      return `SWC_${this.ServerClockWorkerUUID}`;
    },
    UpdatedServerClock(): Date {
      return this.LastServerClock
        ? getIstTimeFromTimestamp(this.LastServerClock.Timestamp)
        : new Date(0);
    },
    UpdatedServerDate(): Date {
      const serverDate = deepCopyDate(this.UpdatedServerClock);
      serverDate.setHours(0, 0, 0, 0);
      return serverDate;
    },
    UpdatedServerDatetimeString(): string | null {
      const isoString = getISODateString(this.UpdatedServerClock);
      return isoString ? isoString : null;
    },
  },
  actions: {
    async pingServerAndInitializeClock(): Promise<number> {
      if (serverClockPingPromise !== null) {
        return serverClockPingPromise;
      }
      serverClockPingPromise = this.performServerClockPing();
      try {
        return await serverClockPingPromise;
      } finally {
        serverClockPingPromise = null;
      }
    },
    async performServerClockPing(): Promise<number> {
      this.resetServerClock();
      const apiServicesStore = useApiServicesStore();
      const { unverifiedUserApi } = apiServicesStore;
      const response = await unverifiedUserApi.getServerClock();
      if (response.isSuccess) {
        this.LastServerClock = response.data as ServerClock;
        this._ServerTimestamp = this.LastServerClock.Timestamp;
        this.startServerClockWorker();
      }
      return response.statusCode;
    },
    // The worker only ticks the fetched timestamp forward — the timestamp itself is already
    // published by the time this runs. A blocked or missing worker therefore costs the forward
    // tick and nothing else, so it must never turn a successful ping into a failed one (a throw
    // here used to escape pingServerAndInitializeClock and leave every waiter hanging).
    startServerClockWorker() {
      try {
        this.ServerClockWorkerUUID = uuidv4().replace(/-/g, "").slice(0, 8);
        this.ServerClockWorker = new Worker("/serverClockWorker.js");
        (window as any)[this.CurrentWorkerKey] = this.ServerClockWorker;
        this.ServerClockWorker.addEventListener("message", (e) => {
          const { type, timestamp, workerKey } = e.data;
          if (workerKey !== this.CurrentWorkerKey) {
            console.debug(
              `Stopping old worker:: ${workerKey} vs Current: ${this.CurrentWorkerKey}`,
            );
            this.stopWorkerByKey(workerKey);
            return;
          }
          if (type === "CLOCK_UPDATE") {
            this._ServerTimestamp = timestamp;
          }
        });
        this.ServerClockWorker.postMessage({
          type: "START_CLOCK",
          data: {
            workerKey: this.CurrentWorkerKey,
            initialServerTimestamp: this._ServerTimestamp,
          },
        });
      } catch (error) {
        this.ServerClockWorker = null;
        const reason = error instanceof Error ? error.message : String(error);
        console.warn(`Server clock worker unavailable; clock will not advance: ${reason}`);
      }
    },
    resetServerClock() {
      this.stopWorkerByKey(this.CurrentWorkerKey);
      if (this.ServerClockWorker) {
        this.stopServerClockWorker(this.ServerClockWorker);
      }
      this.$reset();
    },
    stopWorkerByKey(workerKey: string) {
      const worker = (window as any)[workerKey];
      if (worker) {
        this.stopServerClockWorker(worker);
      }
    },
    stopServerClockWorker(worker: Worker) {
      try {
        worker.postMessage({ type: "STOP_CLOCK" });
        worker.terminate();
        delete (window as any)[this.CurrentWorkerKey];
      } catch (err) {
        console.warn("Failed to stop server clock worker:", err);
      }
    },
    async getServerTimestamp(): Promise<number> {
      await this._waitForServerSync();
      return this._ServerTimestamp;
    },
    getTimeRemaining(endDatetime: string, maxDurationMinutes: number) {
      if (this._ServerTimestamp === null) {
        return null;
      }
      const endTimestamp = getTimestampFromDatetime(endDatetime);
      const timeDifference = differenceInSeconds(endTimestamp, this._ServerTimestamp);
      const timeRemaining = Math.min(maxDurationMinutes * 60, timeDifference);
      return Math.max(0, timeRemaining);
    },
    getTimeElapsed(startTimestamp: number) {
      if (this._ServerTimestamp === null) {
        return null;
      }
      const timeDifference = differenceInSeconds(this._ServerTimestamp, startTimestamp);
      return Math.max(0, timeDifference);
    },
    // Settles when the ping settles, whether or not it succeeded. It previously waited on a
    // $subscribe that could only fire on a SUCCESSFUL ping, so any failure — server down, 404
    // after an API major version was retired, 429 under load — left this promise pending
    // forever, and with it every awaiting getServerTimestamp() caller. The worst of those is
    // sessionStore.beforeNavigateStart(): the router guard never returned, so the app sat on
    // "Connecting to server..." indefinitely with no timeout and no route to UnderMaintenance.
    // The bound is the 10s axios timeout on getServerClock(), not this subscription.
    //
    // On failure _ServerTimestamp stays 0 and callers get 0 back. That is deliberate: every
    // comparison against it then fails closed (elapsed-time checks read as "not elapsed"), which
    // is the safe direction. Substituting device time would silently defeat the point of having
    // a server clock for timed activities.
    async _waitForServerSync(): Promise<void> {
      if (this._ServerTimestamp > 0) {
        return;
      }
      await this.pingServerAndInitializeClock();
    },
    getTimerDisplay(timeValue: number | null): string | null {
      return timeValue === null ? null : getFormattedDuration(timeValue);
    },
  },
});
